import type {
  WhiteLayerExportPayload,
  WhiteLayerProcessedImagePayload,
  WhiteLayerSettings,
  WhiteLayerSingleImagePayload,
  WhiteLayerGangsheetPayload,
} from "../../types/whiteLayer";

type ExportValidationResult = {
  valid: boolean;
  errors: string[];
};

export function validateExportPayload(
  payload: WhiteLayerExportPayload
): ExportValidationResult {
  const errors: string[] = [];

  if (!payload || typeof payload !== "object") {
    errors.push("Missing export payload.");
    return {
      valid: false,
      errors,
    };
  }

  switch (payload.sourceType) {
    case "processed-image":
      validateProcessedPayload(payload, errors);
      break;

    case "single-image":
      validateSingleImagePayload(payload, errors);
      break;

    case "gangsheet":
      validateGangsheetPayload(payload, errors);
      break;

    default:
      errors.push("Unsupported export source type.");
      break;
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

function validateProcessedPayload(
  payload: WhiteLayerProcessedImagePayload,
  errors: string[]
) {
  if (!payload.width || payload.width <= 0) {
    errors.push("Processed export width is missing or invalid.");
  }

  if (!payload.height || payload.height <= 0) {
    errors.push("Processed export height is missing or invalid.");
  }

  if (!payload.fileName?.trim()) {
    errors.push("Processed export file name is missing.");
  }

  if (!payload.originalImageBase64?.trim()) {
    errors.push("Processed original image buffer is missing.");
  }

  if (!payload.w1MaskBase64?.trim()) {
    errors.push("Processed W1 mask buffer is missing.");
  }

  if (!payload.exportFormat) {
    errors.push("Processed export format is missing.");
  } else if (payload.exportFormat !== "png" && payload.exportFormat !== "tiff") {
    errors.push("Processed export format must be png or tiff.");
  }
}

function validateSingleImagePayload(
  payload: WhiteLayerSingleImagePayload,
  errors: string[]
) {
  if (!payload.imageBase64?.trim()) {
    errors.push("Image base64 is missing.");
  }

  if (!payload.imageName?.trim()) {
    errors.push("Image name is missing.");
  }

  if (!payload.mimeType?.trim()) {
    errors.push("Image mime type is missing.");
  }

  if (!payload.width || payload.width <= 0) {
    errors.push("Image width is missing or invalid.");
  }

  if (!payload.height || payload.height <= 0) {
    errors.push("Image height is missing or invalid.");
  }

  validateSettings(payload.settings, errors);
}

function validateGangsheetPayload(
  payload: WhiteLayerGangsheetPayload,
  errors: string[]
) {
  if (!payload.sheetWidth || payload.sheetWidth <= 0) {
    errors.push("Gangsheet width is missing or invalid.");
  }

  if (!payload.sheetHeight || payload.sheetHeight <= 0) {
    errors.push("Gangsheet height is missing or invalid.");
  }

  if (!payload.dpi || payload.dpi <= 0) {
    errors.push("Gangsheet DPI is missing or invalid.");
  }

  if (!payload.items?.length) {
    errors.push("Gangsheet items are missing.");
  }

  validateSettings(payload.settings, errors);
}

function validateSettings(
  settings: WhiteLayerSettings | undefined,
  errors: string[]
) {
  if (!settings) {
    errors.push("White layer settings are missing.");
    return;
  }

  validateNumberRange(settings.whiteStrength, "White Strength", 0, 100, errors);
  validateNumberRange(settings.edgeTightness, "Edge Tightness", 0, 8, errors);
  validateNumberRange(settings.darkReduction, "Dark Reduction", 0, 100, errors);
  validateNumberRange(settings.fadeSupport, "Fade Support", 0, 100, errors);
  validateNumberRange(settings.noiseCleanup, "Noise Cleanup", 0, 30, errors);
}

function validateNumberRange(
  value: number,
  label: string,
  min: number,
  max: number,
  errors: string[]
) {
  if (typeof value !== "number" || Number.isNaN(value)) {
    errors.push(`${label} must be a valid number.`);
    return;
  }

  if (value < min || value > max) {
    errors.push(`${label} must be between ${min} and ${max}.`);
  }
}